import { createSelector } from '@reduxjs/toolkit';
import { statusAdapter } from '../adapters/common/status-adapter';
import { devBoxAddOnsAdapter } from '../adapters/dev-box-add-ons-adapters';
import { DevBoxTunnelPhase } from '../store/add-ons-store';
import { StoreState } from '../store/store-state';
import { StoreStateSelector, createMapFromEntitiesSelector } from './common';

/**
 * Basic selectors
 */

export const getDevBoxTunnelPhases: StoreStateSelector<{ [key: string]: DevBoxTunnelPhase }> = (store) =>
    store.addOnsStore.data.devBoxTunnelPhases;

/**
 * Entity state selectors
 */

const devBoxAddOnSelectors = devBoxAddOnsAdapter.getSelectors<StoreState>(
    (store) => store.addOnsStore.data.devBoxAddOns
);

const statusesForListAddOnsSelectors = statusAdapter.getSelectors<StoreState>(
    (store) => store.addOnsStore.status.listDevBoxAddOns
);

const statusesForEnableAddOnsSelectors = statusAdapter.getSelectors<StoreState>(
    (store) => store.addOnsStore.status.enableDevBoxAddOn
);

const statusesForEnableAddOnsWithPollingSelectors = statusAdapter.getSelectors<StoreState>(
    (store) => store.addOnsStore.status.enableDevBoxAddOnWithPolling
);

/**
 * Composed selectors
 */

export const getAddOnsByDevBoxId = createMapFromEntitiesSelector(devBoxAddOnSelectors.selectAll);

export const getStatusesForListAddOnsByDevBoxId = createMapFromEntitiesSelector(
    statusesForListAddOnsSelectors.selectAll
);

export const getStatusesForEnableAddOnsByDevBoxId = createMapFromEntitiesSelector(
    statusesForEnableAddOnsSelectors.selectAll
);

export const getStatusesForEnableAddOnsWithPollingByDevBoxId = createMapFromEntitiesSelector(
    statusesForEnableAddOnsWithPollingSelectors.selectAll
);

/**
 * Phase selectors
 */

export const getDevBoxTunnelPhaseById = (devBoxId: string): StoreStateSelector<DevBoxTunnelPhase | undefined> =>
    createSelector([getDevBoxTunnelPhases], (phases) => phases[devBoxId]);

export const getIsDevBoxInStartPhase = (devBoxId: string): StoreStateSelector<boolean> =>
    createSelector([getDevBoxTunnelPhaseById(devBoxId)], (phase) => phase === DevBoxTunnelPhase.StartDevBox);

export const getIsDevBoxInTunnelEnablingPhase = (devBoxId: string): StoreStateSelector<boolean> =>
    createSelector([getDevBoxTunnelPhaseById(devBoxId)], (phase) => phase === DevBoxTunnelPhase.TunnelEnabling);

export const getIsDevBoxTunnelEnablingFinished = (devBoxId: string): StoreStateSelector<boolean> =>
    createSelector([getDevBoxTunnelPhaseById(devBoxId)], (phase) => phase === DevBoxTunnelPhase.TunnelEnablingFinished);

export const getIsDevBoxTunnelNotEnabled = (devBoxId: string): StoreStateSelector<boolean> =>
    createSelector([getDevBoxTunnelPhaseById(devBoxId)], (phase) => phase === DevBoxTunnelPhase.TunnelNotEnabled);

/**
 * Combined phase selector for better performance
 */
export const getDevBoxTunnelPhaseStates = (
    devBoxId: string
): StoreStateSelector<{
    isInStartPhase: boolean;
    isInTunnelEnablingPhase: boolean;
    isTunnelEnablingFinished: boolean;
    isTunnelNotEnabled: boolean;
}> =>
    createSelector([getDevBoxTunnelPhaseById(devBoxId)], (phase) => ({
        isInStartPhase: phase === DevBoxTunnelPhase.StartDevBox,
        isInTunnelEnablingPhase: phase === DevBoxTunnelPhase.TunnelEnabling,
        isTunnelEnablingFinished: phase === DevBoxTunnelPhase.TunnelEnablingFinished,
        isTunnelNotEnabled: phase === DevBoxTunnelPhase.TunnelNotEnabled,
    }));
